import React, { useMemo, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { useAiChatRouteSummary } from '../../contexts/AiChatRouteContext'
import EntityComments from '../../components/EntityComments'
import EntityHistory from '../../components/EntityHistory'
import { Button } from '../../components/ui'
import Icon from '../../components/Icon'
import { DetailPageHeader, DetailSection, InfoGrid, KpiStrip, SummaryCard, BudgetMatrix } from './detail-pages/DetailPageScaffold'
import { formatRub, formatRubTable } from '../../utils/formatRub'
import { getBudgetExpenseKind, getBudgetExpenseOrder } from './budgetExpenseVisuals'
import { BudgetStageTable, BudgetColumnsMenu } from './BudgetCard'
import { enrichBudgetTableWithSubTeams } from './budgetTableSubTeams'
import { buildGroupedObjectBudgetTable, getObjectBudgetColumnGroups } from './objectBudgetGrouping'
import './Company.css'

const OBJECT_BUDGET_METRIC_IDS = ['plan', 'percent', 'fact']

const PRODUCTION_DEPARTMENT_TYPE = 'Производственное'
const SERVICE_SECTION = 'Обслуживание'
const OTHER_EXPENSE_ORDER = ['Налоги (+9%)', 'Налоги (+5%)', 'Налоги', 'Производственные расходы', 'Произв-е расходы', 'Произв-ые расходы', 'Непроизводственные расходы', 'Непроизв-е расходы', 'Непроиз-ые расходы']

function normalizeName(value) {
  return String(value || '').trim()
}

function getDepartmentSection(type) {
  return type === PRODUCTION_DEPARTMENT_TYPE ? 'Производство' : SERVICE_SECTION
}

function getOtherExpenseOrder(label) {
  const index = OTHER_EXPENSE_ORDER.findIndex((item) => item.toLowerCase() === String(label || '').toLowerCase())
  return index === -1 ? OTHER_EXPENSE_ORDER.length : index
}

function getStageTitle(stage, index) {
  return normalizeName(stage.stage_name || stage.stage_title || stage.distribution_type || stage.stage_id) || `Стадия ${index + 1}`
}

function getStageType(stage, index) {
  return normalizeName(stage.stage_type || stage.type) || getStageTitle(stage, index)
}

function buildObjectBudget(stagesIncome = [], departmentsOrder = [], formatValue = formatRub, onStageClick) {
  const columns = stagesIncome.map((stage, idx) => ({
    key: stage.stage_id || `stage-${idx}`,
    label: getStageType(stage, idx),
    subLabel: getStageTitle(stage, idx),
    onSubLabelClick: stage.stage_id ? () => onStageClick?.(stage.stage_id) : undefined,
  }))

  const departmentMeta = new Map()
  departmentsOrder.forEach((department, order) => {
    const name = normalizeName(department.name)
    if (!name) return
    departmentMeta.set(name, {
      order,
      type: department.department_type || 'Обслуживание',
      section: getDepartmentSection(department.department_type),
    })
  })

  const byDepartment = new Map()

  stagesIncome.forEach((stage, stageIdx) => {
    ;(stage.departments || []).forEach((dept) => {
      const name = normalizeName(dept.department_name) || 'Без подразделения'
      if (!byDepartment.has(name)) {
        const meta = departmentMeta.get(name)
        byDepartment.set(name, {
          label: name,
          values: Array(columns.length).fill(0),
          section: meta?.section || getDepartmentSection(dept.department_type),
          order: meta?.order ?? Number.MAX_SAFE_INTEGER,
        })
      }
      byDepartment.get(name).values[stageIdx] += Number(dept.total_income || 0)
    })

    ;(stage.other_expenses || []).forEach((expense) => {
      const name = normalizeName(expense.expense_type) || 'Прочие расходы'
      const expenseKind = getBudgetExpenseKind(expense)
      if (!byDepartment.has(name)) {
        byDepartment.set(name, {
          label: name,
          values: Array(columns.length).fill(0),
          section: SERVICE_SECTION,
          order: 100000 + getBudgetExpenseOrder(expenseKind) * 1000 + getOtherExpenseOrder(name),
          isOtherExpense: true,
          expenseKind,
        })
      }
      byDepartment.get(name).values[stageIdx] += Number(expense.amount || 0)
    })
  })

  const rows = Array.from(byDepartment.values())
    .map((row) => ({
      key: row.label,
      label: row.label,
      section: row.section,
      order: row.order,
      isOtherExpense: row.isOtherExpense,
      expenseKind: row.expenseKind,
      sectionOrder: row.section === 'Производство' ? 0 : 1,
      values: row.values,
      total: row.values.reduce((sum, value) => sum + Number(value || 0), 0),
      formatValue,
    }))
    .filter((row) => row.total > 0)
    .sort((a, b) => (
      a.sectionOrder - b.sectionOrder
      || a.order - b.order
      || Number(Boolean(a.isOtherExpense)) - Number(Boolean(b.isOtherExpense))
      || a.label.localeCompare(b.label, 'ru')
    ))
    .map(({ sectionOrder, order, isOtherExpense, ...row }) => row)

  return { columns, rows }
}

function ObjectCard() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { setHumanSummary } = useAiChatRouteSummary()
  const [object, setObject] = useState(null)
  const canUpdate = user?.permissions?.['objects.update']
  const canCreateContract = user?.permissions?.['contracts.create']
  const canCreateStage = user?.permissions?.['stages.create']
  const canUpdateStage = user?.permissions?.['stages.update']
  const [loading, setLoading] = useState(true)
  const [selectedStageId, setSelectedStageId] = useState(null)
  const [visibleBudgetColumnIds, setVisibleBudgetColumnIds] = useState(null)
  const [visibleBudgetMetricIds, setVisibleBudgetMetricIds] = useState(OBJECT_BUDGET_METRIC_IDS)

  usePageHeader({ title: null, hideTopHeader: true }, [])

  useEffect(() => {
    fetchObject()
  }, [id])

  useEffect(() => {
    if (!object || String(object.id) !== String(id)) {
      setHumanSummary(null)
      return undefined
    }
    const name = (object.name || '').trim()
    if (!name) {
      setHumanSummary(null)
      return undefined
    }
    setHumanSummary(`Объект: ${name}`)
    return () => setHumanSummary(null)
  }, [object, id, setHumanSummary])

  const objectStages = useMemo(() => object?.object_stages || [], [object])
  const selectedStage = objectStages.find((stage) => String(stage.id) === String(selectedStageId)) || objectStages[0] || null

  useEffect(() => {
    if (!objectStages.length) {
      if (selectedStageId) setSelectedStageId(null)
      return
    }
    if (!selectedStageId || !objectStages.some((stage) => String(stage.id) === String(selectedStageId))) {
      setSelectedStageId(objectStages[0].id)
    }
  }, [objectStages, selectedStageId])

  const fetchObject = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/company/objects/${id}`)
      if (response.data && response.data.object) {
        setObject(response.data.object)
      }
    } catch (error) {
      console.error('Error fetching object:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const dateOnlyMatch = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch
      return `${day}.${month}.${year}`
    }
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU')
  }

  const menuIcon = (name) => <Icon name={name} size={14} />
  const contracts = object?.contracts || []
  const totalContractsAmount = contracts.reduce((sum, contract) => sum + Number(contract.total_amount || 0), 0)
  const totalActsAmount = contracts.reduce((sum, contract) => sum + Number(contract.acts_amount || 0), 0)
  const objectRemainder = Math.max(0, totalContractsAmount - totalActsAmount)
  const objectBudget = buildObjectBudget(
    object?.stages_income || [],
    object?.departments_order || [],
    formatRubTable,
    (stageId) => navigate(`/stage/${encodeURIComponent(stageId)}`)
  )
  const objectBudgetTable = useMemo(
    () => buildGroupedObjectBudgetTable(
      enrichBudgetTableWithSubTeams(object?.object_budget_table, object?.stages_income),
    ),
    [object?.object_budget_table, object?.stages_income],
  )
  const objectBudgetColumns = useMemo(() => objectBudgetTable?.columns || [], [objectBudgetTable])
  const objectBudgetColumnGroups = useMemo(
    () => getObjectBudgetColumnGroups(objectBudgetColumns),
    [objectBudgetColumns],
  )
  const allObjectBudgetColumnIds = useMemo(
    () => objectBudgetColumns.map((column) => column.id),
    [objectBudgetColumns],
  )
  // null — пользователь ещё не менял видимость, показываем все колонки без мерцания
  const effectiveBudgetColumnIds = visibleBudgetColumnIds ?? allObjectBudgetColumnIds

  useEffect(() => {
    setVisibleBudgetColumnIds(null)
  }, [allObjectBudgetColumnIds])

  const handleBudgetColumnToggle = (columnId) => {
    setVisibleBudgetColumnIds((prev) => {
      const base = prev ?? allObjectBudgetColumnIds
      return base.includes(columnId)
        ? base.filter((id) => id !== columnId)
        : [...base, columnId]
    })
  }

  const handleBudgetMetricToggle = (metricId) => {
    setVisibleBudgetMetricIds((prev) => (
      prev.includes(metricId)
        ? prev.filter((id) => id !== metricId)
        : [...prev, metricId]
    ))
  }

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  if (!object) {
    return <div className="no-data">Объект не найден</div>
  }

  return (
    <div className="company-page entity-detail-page entity-detail-redesign object-detail-redesign">
      <DetailPageHeader
        breadcrumbs={[
          { label: 'Клиенты', to: '/company/clients' },
          { label: object.client_name || 'Клиент', to: object.client_id ? `/client/${object.client_id}` : undefined },
          { label: 'Объекты', to: '/company/objects' },
          { label: object.name || 'Объект' }
        ]}
        title={object.name || 'Объект'}
        status={object.status || (contracts.length > 0 ? 'в работе' : 'новый')}
        subtitle={`${object.client_name || 'Клиент'} — ${object.type || 'объект'}`}
        onBack={() => navigate('/company/objects')}
        actions={[
          canUpdate ? {
            id: 'edit-object',
            label: 'Редактировать объект',
            icon: menuIcon('edit'),
            onSelect: () => navigate(`/company/objects/${id}/edit`, { state: { objectId: id, clientId: object.client_id } })
          } : null,
          {
            id: 'object-report',
            label: 'Сформировать отчёт по объекту',
            icon: menuIcon('file'),
            onSelect: () => {}
          },
          canCreateStage ? {
            id: 'create-stage',
            label: 'Создать стадию',
            icon: menuIcon('plus'),
            onSelect: () => navigate('/company/budgets/new', { state: { objectId: id } })
          } : null,
          {
            id: 'object-export',
            label: 'Экспортировать',
            icon: menuIcon('export'),
            onSelect: () => {}
          }
        ]}
      />

      <DetailSection title="Паспорт объекта">
        <InfoGrid
          items={[
            { label: 'Название', value: object.name || '-' },
            { label: 'Адрес', value: object.address || '-', muted: !object.address },
            { label: 'Клиент', value: <button type="button" className="entity-text-link" onClick={() => navigate(`/client/${object.client_id}`)}>{object.client_name || '-'}</button> },
            { label: 'Тип объекта', value: object.type || '-' },
            object.living_area ? { label: 'Квадратура жилой площади', value: `${object.living_area} м²` } : null,
            object.parking_spaces !== null ? { label: 'Количество машиномест', value: object.parking_spaces } : null,
            !object.cost_manual && object.estimated_square_price != null && object.estimated_square_price !== '' ? { label: 'Стоимость за м²', value: <span className="amount-mono">{formatRub(object.estimated_square_price)}</span> } : null,
            !object.cost_manual && object.estimated_parking_price != null && object.estimated_parking_price !== '' ? { label: 'Стоимость за машиноместо', value: <span className="amount-mono">{formatRub(object.estimated_parking_price)}</span> } : null,
          ]}
        />
      </DetailSection>

      <KpiStrip
        items={[
          { label: 'Договоров', value: String(contracts.length), tone: 'info' },
          { label: 'Сумма по договорам', value: formatRub(totalContractsAmount), tone: 'accent' },
          { label: 'Заактировано', value: formatRub(totalActsAmount), tone: 'success' },
          { label: 'Остаток', value: formatRub(objectRemainder), tone: 'warning' },
        ]}
      />

      <div className="object-detail-columns">
        <DetailSection
          title="Договоры"
          actions={
            canCreateContract ? (
              <Button
                onClick={() => navigate('/company/contracts/new', {
                  state: {
                    clientId: object.client_id,
                    objectId: id
                  }
                })}
                variant="primary"
                className="btn-add"
              >
                <Icon name="plus" size={12} /> Добавить
              </Button>
            ) : null
          }
        >
          {contracts.length > 0 ? (
            <div className="list-cards">
              {contracts.map((contract, contractIdx) => {
                const amount = Number(contract.total_amount || 0)
                const acted = Number(contract.acts_amount || 0)
                const progress = amount > 0 ? Math.round((acted / amount) * 100) : 0
                return (
                  <SummaryCard
                    key={`${contract.id || 'contract'}-${contractIdx}`}
                    title={contract.number || 'Договор'}
                    status={contract.status || undefined}
                    meta={`до ${formatDate(contract.contract_date)}`}
                    amount={formatRub(contract.total_amount)}
                    sub={`заактировано ${formatRub(contract.acts_amount)}`}
                    progress={progress}
                    progressTone={progress >= 90 ? 'green' : progress > 0 ? 'orange' : 'grey'}
                    to={`/contract/${contract.id}`}
                  />
                )
              })}
            </div>
          ) : (
            <p className="info-note">Договоры не найдены</p>
          )}
        </DetailSection>

        <DetailSection
          title="Стадии по договору"
          actions={
            canCreateStage ? (
              <Button
                onClick={() => navigate('/company/budgets/new', { state: { objectId: id } })}
                variant="primary"
                className="btn-add"
              >
                <Icon name="plus" size={12} /> Создать стадию
              </Button>
            ) : null
          }
        >
          {objectStages.length > 0 ? (
            <div className="list-cards object-stage-cards">
              {objectStages.map((stage) => {
                const progress = Number(stage.budgets_total || 0) > 0
                  ? Math.min(100, Math.round((Number(stage.work_totals?.amount_with_vat || 0) / Number(stage.budgets_total || 1)) * 100))
                  : 0
                return (
                  <SummaryCard
                    key={stage.id}
                    title={stage.name || stage.type || 'Этап'}
                    status={stage.status || undefined}
                    meta={stage.planned_date ? `до ${formatDate(stage.planned_date)}` : `${stage.budgets_count || 0} бюджетов`}
                    amount={formatRub(stage.budgets_total)}
                    sub={`${stage.work_items?.length || 0} разделов`}
                    progress={progress}
                    progressTone={stage.status === 'работы завершены' ? 'green' : progress > 0 ? 'orange' : 'grey'}
                    to={`/company/budgets/${encodeURIComponent(stage.public_id || stage.id)}`}
                  />
                )
              })}
            </div>
          ) : (
            <p className="info-note">Стадии не найдены</p>
          )}
        </DetailSection>
      </div>

      <DetailSection
        title="Бюджет объекта"
        actions={objectBudgetColumns.length ? (
          <BudgetColumnsMenu
            columns={objectBudgetColumns}
            columnGroups={objectBudgetColumnGroups}
            visibleColumnIds={effectiveBudgetColumnIds}
            visibleMetricIds={visibleBudgetMetricIds}
            onColumnToggle={handleBudgetColumnToggle}
            onMetricToggle={handleBudgetMetricToggle}
          />
        ) : null}
      >
        {objectBudgetColumns.length ? (
          <BudgetStageTable
            ariaLabel="Бюджет объекта"
            table={objectBudgetTable}
            visibleColumnIds={effectiveBudgetColumnIds}
            visibleMetricIds={visibleBudgetMetricIds}
            showTotalPercent
          />
        ) : (
          <BudgetMatrix
            ariaLabel="Бюджет объекта"
            columns={objectBudget.columns}
            rows={objectBudget.rows}
            totalLabel="Итого"
            emptyText="Бюджет объекта не найден"
          />
        )}
      </DetailSection>

      <DetailSection
        title="Разделы"
        className="object-stage-works-section"
        actions={
          selectedStage && canUpdateStage ? (
            <Button
              variant="secondary"
              onClick={() => navigate(`/company/budgets/${encodeURIComponent(selectedStage.id)}/edit`, { state: { from: 'object', objectId: id } })}
            >
              <Icon name="edit" size={12} /> Редактировать
            </Button>
          ) : null
        }
      >
        {objectStages.length > 0 ? (
          <>
            <div className="object-stage-tabs" role="tablist" aria-label="Разделы по стадиям объекта">
              {objectStages.map((stage) => (
                <button
                  key={stage.id}
                  type="button"
                  role="tab"
                  aria-selected={String(stage.id) === String(selectedStage?.id)}
                  className={String(stage.id) === String(selectedStage?.id) ? 'is-active' : ''}
                  onClick={() => setSelectedStageId(stage.id)}
                >
                  {stage.name || stage.type || 'Стадия'}
                </button>
              ))}
            </div>
            {selectedStage?.work_items?.length ? (
              <div className="object-stage-work-table-wrap">
                <table className="object-stage-work-table" aria-label="Разделы стадии">
                  <thead>
                    <tr>
                      <th>Раздел</th>
                      <th>Срок</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStage.work_items.map((item) => (
                      <tr key={item.id || item.name}>
                        <td>{item.name}</td>
                        <td>
                          {item.due_date || item.end_date ? (
                            <span className="object-stage-work-date-range">
                              {formatDate(item.due_date || item.end_date)}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="object-stage-work-summary">
                  <span>Итого разделов: {selectedStage.work_items.length}</span>
                  {selectedStage.handover_date ? <span>{`Сдача: ${formatDate(selectedStage.handover_date)}`}</span> : null}
                </div>
              </div>
            ) : (
              <div className="object-stage-works-empty">
                <p className="info-note">Разделы для выбранной стадии пока не заданы</p>
                {canUpdateStage && selectedStage ? (
                  <Button
                    variant="primary"
                    className="btn-add"
                    onClick={() => navigate(`/company/budgets/${encodeURIComponent(selectedStage.id)}/edit`, { state: { from: 'object', objectId: id } })}
                  >
                    <Icon name="plus" size={12} /> Добавить
                  </Button>
                ) : null}
              </div>
            )}
          </>
        ) : (
          <p className="info-note">Разделы появятся после создания стадии объекта</p>
        )}
      </DetailSection>

      <EntityComments entityType="objects" entityId={id} />
      <EntityHistory entityType="objects" entityId={id} />
    </div>
  )
}

export default ObjectCard
